import { StandardEvents } from '@shopify/events';

class MalzauberMotifGallery extends HTMLElement {
  lifecycle = 0;
  generation = 0;
  initialized = false;
  phase = 'loading';
  activeSeason = '';
  selectedMotif = '';
  selectedSize = '';
  confirmed = null;
  scrollingTrack = null;
  pointerTrack = null;
  touchTrack = null;
  pointerId = null;
  settleTimer = null;
  layoutFrame = null;
  refreshFrame = null;
  controller = null;
  observer = null;
  section = null;
  motifs = new Map();
  panels = new Map();
  remembered = new Map();
  guardedRoots = new Map();
  programmaticTracks = new Map();
  lastScrollPositions = new Map();
  originalImages = new WeakMap();

  connectedCallback() {
    const lifecycle = ++this.lifecycle;
    this.controller = new AbortController();
    const documentReady = document.readyState === 'loading'
      ? new Promise((resolve) => document.addEventListener('DOMContentLoaded', resolve, {
          once: true,
          signal: this.controller.signal,
        }))
      : Promise.resolve();

    Promise.all([
      documentReady,
      customElements.whenDefined('variant-picker'),
      customElements.whenDefined('product-form-component'),
    ]).then(() => {
      if (this.isConnected && lifecycle === this.lifecycle) this.initialize();
    });
  }

  disconnectedCallback() {
    this.lifecycle += 1;
    this.generation += 1;
    this.initialized = false;
    this.controller?.abort();
    this.observer?.disconnect();
    window.clearTimeout(this.settleTimer);
    window.cancelAnimationFrame(this.layoutFrame);
    window.cancelAnimationFrame(this.refreshFrame);
    this.settleTimer = null;
    this.layoutFrame = null;
    this.refreshFrame = null;
    this.restorePurchaseRoots();
    this.section?.classList.remove('mzm-enhanced');
    this.programmaticTracks.clear();
    this.lastScrollPositions.clear();
    this.scrollingTrack = null;
    this.pointerTrack = null;
    this.touchTrack = null;
    this.pointerId = null;
    this.confirmed = null;
  }

  initialize() {
    this.section = this.closest('.shopify-section');
    if (!this.section || !this.dataset.productId) return;

    let data;
    try {
      data = JSON.parse(this.querySelector('script[type="application/json"][data-mzm-data]')?.textContent || '');
    } catch {
      return;
    }

    if (!data || !Array.isArray(data.motifs) || !data.motifs.length) return;
    if (!['loading', 'error', 'retry'].every((key) => typeof data.labels?.[key] === 'string' && data.labels[key])) return;

    this.labels = data.labels;
    this.motifs = new Map(data.motifs
      .filter((motif) => typeof motif.name === 'string' && typeof motif.preview === 'string'
        && ['year', 'autumn', 'christmas'].includes(motif.season))
      .map((motif) => [motif.name, motif]));
    this.panels = new Map(Array.from(this.querySelectorAll('[data-mzm-panel]'))
      .map((panel) => [panel.dataset.mzmPanel, panel]));
    this.summary = this.section.querySelector('[data-mzm-summary]');
    this.status = this.section.querySelector('[data-mzm-status]');
    this.retry = this.section.querySelector('[data-mzm-retry]');

    if (!this.summary || !this.status || !this.retry) return;
    if (!['image', 'name', 'season', 'size'].every((part) => this.summary.querySelector(`[data-mzm-summary-${part}]`))) return;
    if (!this.panels.size || !Array.from(this.panels.values()).every((panel) => panel.querySelector('[data-mzm-track]'))) return;

    const native = this.getNativeSelection();
    if (!native || !this.getProductForms().length || !this.findCard(native.motif)) return;

    this.phase = 'loading';
    this.confirmed = null;
    this.initialized = true;
    this.bindEvents();
    this.renderSelection(native, true);
    this.summary.hidden = false;
    this.retry.textContent = this.labels.retry;
    this.section.classList.add('mzm-enhanced');
    this.updateGuard();
    this.observeSection();

    let resource;
    try {
      resource = JSON.parse(native.picker.querySelector('script[type="application/json"]')?.textContent || 'null');
    } catch {
      resource = null;
    }

    const generation = this.generation;
    const lifecycle = this.lifecycle;
    window.requestAnimationFrame(() => {
      if (!this.isCurrent(generation, lifecycle)) return;
      if (this.resourceMatches(resource, native) && this.formsMatch(resource.id)) {
        this.finishSelection(resource, native, generation);
      } else {
        this.chooseMotif(native.motif, true);
      }
    });
  }

  bindEvents() {
    const { signal } = this.controller;
    this.addEventListener('click', (event) => this.handleGalleryClick(event), { signal });
    this.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
    this.section.addEventListener('click', (event) => this.handleSummaryClick(event), { signal });
    this.section.addEventListener(StandardEvents.productSelect, (event) => this.handleProductSelect(event), {
      capture: true,
      signal,
    });
    window.addEventListener('click', (event) => this.guardPurchaseAttempt(event), { capture: true, signal });
    window.addEventListener('submit', (event) => this.guardPurchaseAttempt(event), { capture: true, signal });
    window.addEventListener('pointerup', (event) => this.endPointer(event), { passive: true, signal });
    window.addEventListener('pointercancel', (event) => this.endPointer(event), { passive: true, signal });
    window.addEventListener('touchend', (event) => this.endTouch(event), { passive: true, signal });
    window.addEventListener('touchcancel', (event) => this.endTouch(event), { passive: true, signal });
    window.addEventListener('resize', () => this.scheduleLayout(), { passive: true, signal });
    window.addEventListener('pageshow', () => this.restoreCurrentSelection(), { signal });
    window.addEventListener('popstate', () => this.restoreCurrentSelection(), { signal });

    for (const panel of this.panels.values()) {
      const track = panel.querySelector('[data-mzm-track]');
      this.lastScrollPositions.set(track, track.scrollLeft);
      track.addEventListener('scroll', () => this.handleScroll(track), { passive: true, signal });
      track.addEventListener('scrollend', () => this.settleTrack(track), { passive: true, signal });
      track.addEventListener('pointerdown', (event) => {
        this.programmaticTracks.delete(track);
        this.pointerTrack = track;
        this.pointerId = event.pointerId;
      }, { passive: true, signal });
      track.addEventListener('touchstart', () => {
        this.programmaticTracks.delete(track);
        this.touchTrack = track;
      }, { passive: true, signal });
      track.addEventListener('wheel', () => this.programmaticTracks.delete(track), { passive: true, signal });
    }
  }

  observeSection() {
    this.observer = new MutationObserver((mutations) => {
      if (mutations.every((mutation) => this.contains(mutation.target) || this.summary.contains(mutation.target))) return;
      if (this.refreshFrame !== null) return;
      this.refreshFrame = window.requestAnimationFrame(() => {
        this.refreshFrame = null;
        if (!this.initialized || !this.isConnected) return;
        if (this.phase === 'ready' && !this.currentSelectionIsConfirmed()) {
          this.phase = 'loading';
          this.confirmed = null;
          this.restoreCurrentSelection();
        }
        this.updateGuard();
      });
    });
    this.observer.observe(this.section, { childList: true, subtree: true });
  }

  getNativeSelection() {
    if (!this.section) return null;
    const picker = Array.from(this.section.querySelectorAll('variant-picker[data-product-id]'))
      .find((element) => this.sameProduct(element.dataset.productId)
        && !element.closest('product-card, quick-add-dialog'));
    const motifSelect = picker?.querySelector('select[name="options[Motiv]"]');
    const sizeSelect = picker?.querySelector('select[name="options[Grösse]"]');
    if (!(motifSelect instanceof HTMLSelectElement) || !(sizeSelect instanceof HTMLSelectElement)) return null;
    if (!this.motifs.has(motifSelect.value) || !sizeSelect.value) return null;
    const selects = Array.from(picker.querySelectorAll('select'));
    const optionIndex = (select) => {
      const position = Number(select.selectedOptions[0]?.dataset.inputId?.split('-')[0]);
      return position > 0 ? position - 1 : selects.indexOf(select);
    };
    return {
      picker,
      motifSelect,
      sizeSelect,
      motif: motifSelect.value,
      size: sizeSelect.value,
      motifIndex: optionIndex(motifSelect),
      sizeIndex: optionIndex(sizeSelect),
    };
  }

  sameProduct(productId) {
    return String(productId || '').split('/').pop() === String(this.dataset.productId).split('/').pop();
  }

  getProductForms() {
    if (!this.section) return [];
    return Array.from(this.section.querySelectorAll('product-form-component'))
      .filter((form) => !form.closest('product-card, quick-add-dialog')
        && (!form.dataset.productId || this.sameProduct(form.dataset.productId)));
  }

  getPurchaseRoots() {
    const roots = this.getProductForms();
    for (const sticky of document.querySelectorAll('sticky-add-to-cart[data-product-id]')) {
      if (this.sameProduct(sticky.dataset.productId)) roots.push(sticky);
    }
    return roots;
  }

  findCard(name) {
    const motif = this.motifs.get(name);
    const panel = motif && this.panels.get(motif.season);
    return panel && Array.from(panel.querySelectorAll('[data-mzm-card]'))
      .find((card) => card.dataset.motif === name);
  }

  activeTrack() {
    return this.panels.get(this.activeSeason)?.querySelector('[data-mzm-track]') || null;
  }

  cardsInTrack(track) {
    return Array.from(track?.querySelectorAll('[data-mzm-card]') || []);
  }

  nearestCard(track) {
    const bounds = track.getBoundingClientRect();
    if (!bounds.width) return this.findCard(this.selectedMotif);
    const center = bounds.left + bounds.width / 2;
    return this.cardsInTrack(track).reduce((nearest, card) => {
      const box = card.getBoundingClientRect();
      const distance = Math.abs(box.left + box.width / 2 - center);
      return !nearest || distance < nearest.distance ? { card, distance } : nearest;
    }, null)?.card || null;
  }

  chooseMotif(name, force = false) {
    const native = this.getNativeSelection();
    const card = this.findCard(name);
    if (!native || !card || !Array.from(native.motifSelect.options).some((option) => option.value === name)) {
      this.failSelection();
      return;
    }

    window.clearTimeout(this.settleTimer);
    this.scrollingTrack = null;
    const changed = native.motif !== name;
    this.renderSelection({ ...native, motif: name }, true);

    if (changed || force) {
      this.phase = 'loading';
      this.confirmed = null;
      this.updateGuard();
      const generation = this.generation;
      native.motifSelect.value = name;
      native.motifSelect.dispatchEvent(new Event('change', { bubbles: true }));
      if (generation === this.generation) this.failSelection();
    } else {
      this.updateGuard();
      if (this.phase === 'ready' && !this.currentSelectionIsConfirmed()) this.chooseMotif(name, true);
    }
  }

  chooseSeason(season) {
    const panel = this.panels.get(season);
    const native = this.getNativeSelection();
    if (!panel || !native) return;
    const allowed = new Set(Array.from(native.motifSelect.options)
      .filter((option) => !option.disabled).map((option) => option.value));
    const remembered = this.remembered.get(season);
    const name = remembered && allowed.has(remembered) && this.findCard(remembered)
      ? remembered
      : Array.from(panel.querySelectorAll('[data-mzm-card]')).find((card) => allowed.has(card.dataset.motif))?.dataset.motif;
    if (name) this.chooseMotif(name);
  }

  renderSelection(selection, align) {
    const motif = this.motifs.get(selection.motif);
    if (!motif) return;
    if (this.activeSeason !== motif.season) {
      window.clearTimeout(this.settleTimer);
      this.scrollingTrack = null;
      this.pointerTrack = null;
      this.touchTrack = null;
      this.pointerId = null;
    }
    this.activeSeason = motif.season;
    this.selectedMotif = motif.name;
    this.selectedSize = selection.size;
    this.remembered.set(motif.season, motif.name);

    for (const [season, panel] of this.panels) panel.hidden = season !== motif.season;
    for (const tab of this.querySelectorAll('[data-mzm-season]')) {
      const selected = tab.dataset.mzmSeason === motif.season;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    }
    for (const card of this.querySelectorAll('[data-mzm-card]')) {
      const selected = card.dataset.motif === motif.name;
      if (selected) card.setAttribute('aria-current', 'true');
      else card.removeAttribute('aria-current');
      const badge = card.querySelector('[data-mzm-card-status]');
      if (badge) badge.hidden = !selected;
    }
    for (const jump of this.querySelectorAll('[data-mzm-jump]')) {
      if (jump.dataset.mzmJump === motif.name) jump.setAttribute('aria-current', 'true');
      else jump.removeAttribute('aria-current');
    }

    const image = this.summary.querySelector('[data-mzm-summary-image]');
    if (image.getAttribute('src') !== motif.preview) {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.setAttribute('src', motif.preview);
    }
    image.setAttribute('alt', motif.name);
    this.setText(this.summary.querySelector('[data-mzm-summary-name]'), motif.name);
    const seasonTab = Array.from(this.querySelectorAll('[data-mzm-season]'))
      .find((tab) => tab.dataset.mzmSeason === motif.season);
    this.setText(this.summary.querySelector('[data-mzm-summary-season]'), motif.seasonLabel || seasonTab?.textContent.trim() || '');
    this.setText(this.summary.querySelector('[data-mzm-summary-size]'), selection.size);
    this.updateControls();
    if (align) this.alignTrack();
  }

  setText(element, value) {
    if (element && element.textContent !== value) element.textContent = value;
  }

  updateControls() {
    const panel = this.panels.get(this.activeSeason);
    const track = this.activeTrack();
    if (!panel || !track) return;
    const cards = this.cardsInTrack(track);
    const index = Math.max(0, cards.findIndex((card) => card.dataset.motif === this.selectedMotif));
    const previous = panel.querySelector('[data-mzm-prev]');
    const next = panel.querySelector('[data-mzm-next]');
    if (previous) previous.disabled = index === 0;
    if (next) next.disabled = index === cards.length - 1;
    this.setText(panel.querySelector('[data-mzm-count]'), `${index + 1} / ${cards.length}`);
  }

  alignTrack() {
    const track = this.activeTrack();
    const card = this.findCard(this.selectedMotif);
    if (!track || !card || !track.clientWidth || this.pointerTrack === track || this.touchTrack === track) return;
    const trackBounds = track.getBoundingClientRect();
    const cardBounds = card.getBoundingClientRect();
    const left = Math.max(0, Math.min(track.scrollWidth - track.clientWidth,
      track.scrollLeft + cardBounds.left - trackBounds.left - (track.clientWidth - cardBounds.width) / 2));
    const token = {};
    this.programmaticTracks.set(track, token);
    track.scrollTo({ left, behavior: 'instant' });
    this.lastScrollPositions.set(track, track.scrollLeft);
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
      if (this.programmaticTracks.get(track) === token) this.programmaticTracks.delete(track);
    }));
  }

  scheduleLayout() {
    window.cancelAnimationFrame(this.layoutFrame);
    this.layoutFrame = window.requestAnimationFrame(() => {
      this.layoutFrame = null;
      if (this.initialized && !this.scrollingTrack) this.alignTrack();
    });
  }

  handleScroll(track) {
    const previous = this.lastScrollPositions.get(track);
    this.lastScrollPositions.set(track, track.scrollLeft);
    if (track !== this.activeTrack() || this.programmaticTracks.has(track)) return;
    if (previous !== undefined && Math.abs(track.scrollLeft - previous) < 0.5) return;
    this.scrollingTrack = track;
    this.updateGuard();
    this.scheduleSettle(track);
  }

  scheduleSettle(track) {
    window.clearTimeout(this.settleTimer);
    this.settleTimer = window.setTimeout(() => this.settleTrack(track), 150);
  }

  settleTrack(track, force = false) {
    if (!this.initialized || track !== this.activeTrack() || this.programmaticTracks.has(track)) return;
    if (!force && track !== this.scrollingTrack) return;
    if (!force && (this.pointerTrack === track || this.touchTrack === track)) return;
    const card = this.nearestCard(track);
    if (card) this.chooseMotif(card.dataset.motif);
  }

  endPointer(event) {
    if (event.pointerId !== this.pointerId) return;
    const track = this.pointerTrack;
    this.pointerTrack = null;
    this.pointerId = null;
    if (track && this.scrollingTrack === track) this.scheduleSettle(track);
    else if (track && !this.touchTrack) this.scheduleLayout();
  }

  endTouch(event) {
    if (!this.touchTrack || event.touches.length) return;
    const track = this.touchTrack;
    this.touchTrack = null;
    if (this.scrollingTrack === track) this.scheduleSettle(track);
    else this.scheduleLayout();
  }

  handleGalleryClick(event) {
    if (!(event.target instanceof Element)) return;
    const view = event.target.closest('[data-mzm-view]');
    if (view && this.contains(view)) {
      event.preventDefault();
      this.togglePhoto(view);
      return;
    }
    const season = event.target.closest('[data-mzm-season]');
    if (season && this.contains(season)) {
      event.preventDefault();
      this.chooseSeason(season.dataset.mzmSeason);
      return;
    }
    const jump = event.target.closest('[data-mzm-jump]');
    if (jump && this.contains(jump)) {
      event.preventDefault();
      this.chooseMotif(jump.dataset.mzmJump);
      return;
    }
    const arrow = event.target.closest('[data-mzm-prev], [data-mzm-next]');
    if (arrow && this.contains(arrow)) {
      event.preventDefault();
      this.moveBy(arrow.hasAttribute('data-mzm-next') ? 1 : -1);
    }
  }

  handleSummaryClick(event) {
    if (!(event.target instanceof Element)) return;
    const retry = event.target.closest('[data-mzm-retry]');
    if (retry === this.retry) {
      event.preventDefault();
      const native = this.getNativeSelection();
      if (native) this.chooseMotif(native.motif, true);
      return;
    }
    const edit = event.target.closest('[data-mzm-edit]');
    if (edit && this.summary.contains(edit)) {
      event.preventDefault();
      this.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
        block: 'start',
      });
      this.activeTrack()?.focus({ preventScroll: true });
    }
  }

  togglePhoto(button) {
    const card = button.closest('[data-mzm-card]');
    const image = card?.querySelector('[data-mzm-image]');
    if (!image || !button.dataset.mzmSrc) return;
    const buttons = Array.from(card.querySelectorAll('[data-mzm-view]'));
    if (buttons.length > 1 && button.getAttribute('aria-pressed') === 'true') return;
    if (!this.originalImages.has(image)) {
      this.originalImages.set(image, Object.fromEntries(['src', 'srcset', 'sizes', 'alt']
        .map((attribute) => [attribute, image.getAttribute(attribute)])));
    }
    const restore = buttons.length === 1 && button.getAttribute('aria-pressed') === 'true';
    for (const other of buttons) {
      other.setAttribute('aria-pressed', String(!restore && other === button));
    }
    if (restore) {
      for (const [attribute, value] of Object.entries(this.originalImages.get(image))) {
        if (value === null) image.removeAttribute(attribute);
        else image.setAttribute(attribute, value);
      }
    } else {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.setAttribute('src', button.dataset.mzmSrc);
      image.setAttribute('alt', button.dataset.mzmAlt || card.dataset.motif);
    }
  }

  handleKeydown(event) {
    if (!(event.target instanceof Element)) return;
    const tab = event.target.closest('[data-mzm-season]');
    if (tab && this.contains(tab)) {
      const tabs = Array.from(this.querySelectorAll('[data-mzm-season]'));
      let index = tabs.indexOf(tab);
      if (event.key === 'ArrowRight') index = (index + 1) % tabs.length;
      else if (event.key === 'ArrowLeft') index = (index - 1 + tabs.length) % tabs.length;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = tabs.length - 1;
      else if (!['Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      this.chooseSeason(tabs[index].dataset.mzmSeason);
      tabs[index].focus({ preventScroll: true });
      return;
    }
    const track = event.target.closest('[data-mzm-track]');
    if (!track || track !== this.activeTrack() || event.target.closest('button, a, input, select, textarea')) return;
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    if (event.key === 'Home' || event.key === 'End') {
      const cards = this.cardsInTrack(track);
      const card = event.key === 'Home' ? cards[0] : cards.at(-1);
      if (card) this.chooseMotif(card.dataset.motif);
    } else {
      this.moveBy(event.key === 'ArrowRight' ? 1 : -1);
    }
  }

  moveBy(direction) {
    const track = this.activeTrack();
    const cards = this.cardsInTrack(track);
    const current = this.scrollingTrack ? this.nearestCard(track)?.dataset.motif : this.selectedMotif;
    const index = cards.findIndex((card) => card.dataset.motif === current);
    const next = cards[Math.max(0, Math.min(cards.length - 1, index + direction))];
    if (next) this.chooseMotif(next.dataset.motif);
  }

  handleProductSelect(event) {
    const native = this.getNativeSelection();
    if (!native || event.target !== native.picker || !event.promise?.then) return;
    const generation = ++this.generation;
    const lifecycle = this.lifecycle;
    this.phase = 'loading';
    this.confirmed = null;
    this.renderSelection(native, !this.scrollingTrack);
    this.updateGuard();

    Promise.resolve(event.promise).then(async ({ detail }) => {
      if (!this.isCurrent(generation, lifecycle)) return;
      const resource = detail?.resource;
      if (detail?.productId && !this.sameProduct(detail.productId)) {
        this.failSelection();
        return;
      }
      if (!this.resourceMatches(resource, native)) {
        this.failSelection();
        return;
      }
      await new Promise((resolve) => window.requestAnimationFrame(resolve));
      if (!this.isCurrent(generation, lifecycle)) return;
      const current = this.getNativeSelection();
      if (!current || current.motif !== native.motif || current.size !== native.size || !this.formsMatch(resource.id)) {
        this.failSelection();
        return;
      }
      this.finishSelection(resource, current, generation);
    }).catch(() => {
      if (this.isCurrent(generation, lifecycle)) this.failSelection();
    });
  }

  isCurrent(generation, lifecycle) {
    return this.initialized && this.isConnected && generation === this.generation && lifecycle === this.lifecycle;
  }

  resourceMatches(resource, selection) {
    return resource && resource.id != null && Array.isArray(resource.options)
      && resource.options[selection.motifIndex] === selection.motif
      && resource.options[selection.sizeIndex] === selection.size;
  }

  formsMatch(variantId) {
    const forms = this.getProductForms();
    return forms.length > 0 && forms.every((form) => {
      const input = form.querySelector('input[name="id"]');
      return input && String(input.value) === String(variantId);
    });
  }

  finishSelection(resource, selection, generation) {
    if (generation !== this.generation) return;
    if (resource.available !== true) {
      this.failSelection();
      return;
    }
    this.confirmed = { id: String(resource.id), motif: selection.motif, size: selection.size };
    this.phase = 'ready';
    this.renderSelection(selection, !this.scrollingTrack);
    this.updateGuard();
  }

  currentSelectionIsConfirmed() {
    const current = this.getNativeSelection();
    return Boolean(this.confirmed && current && current.motif === this.confirmed.motif
      && current.size === this.confirmed.size && this.formsMatch(this.confirmed.id));
  }

  failSelection() {
    if (!this.initialized) return;
    this.phase = 'error';
    this.confirmed = null;
    this.updateGuard();
  }

  updateGuard() {
    if (!this.initialized) return;
    const busy = this.phase === 'loading' || Boolean(this.scrollingTrack);
    const blocked = this.phase !== 'ready' || Boolean(this.scrollingTrack);
    this.summary.setAttribute('aria-busy', String(busy));
    this.setText(this.status, this.phase === 'error' ? this.labels.error : busy ? this.labels.loading : '');
    this.retry.hidden = this.phase !== 'error';

    if (!blocked) {
      this.restorePurchaseRoots();
      return;
    }
    for (const root of this.getPurchaseRoots()) {
      if (!this.guardedRoots.has(root)) this.guardedRoots.set(root, root.inert);
      root.inert = true;
    }
  }

  restorePurchaseRoots() {
    for (const [root, inert] of this.guardedRoots) root.inert = inert;
    this.guardedRoots.clear();
  }

  guardPurchaseAttempt(event) {
    if (!this.initialized || !(event.target instanceof Element)) return;
    const root = event.target.closest('product-form-component, sticky-add-to-cart');
    if (!root || !this.getPurchaseRoots().includes(root)) return;
    const track = this.activeTrack();
    const visible = track && this.nearestCard(track);
    if (visible && (this.scrollingTrack || visible.dataset.motif !== this.selectedMotif)) {
      this.programmaticTracks.delete(track);
      this.settleTrack(track, true);
    }
    if (this.phase === 'ready' && !this.currentSelectionIsConfirmed()) {
      const current = this.getNativeSelection();
      if (current) this.chooseMotif(current.motif, true);
      else this.failSelection();
    }
    if (this.phase !== 'ready' || this.scrollingTrack) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  restoreCurrentSelection() {
    const lifecycle = this.lifecycle;
    window.requestAnimationFrame(() => {
      if (!this.initialized || !this.isConnected || lifecycle !== this.lifecycle || this.scrollingTrack) return;
      const current = this.getNativeSelection();
      if (!current) {
        this.failSelection();
        return;
      }
      this.renderSelection(current, true);
      if (this.phase === 'ready' && this.currentSelectionIsConfirmed()) this.updateGuard();
      else this.chooseMotif(current.motif, true);
    });
  }
}

if (!customElements.get('malzauber-motif-gallery')) {
  customElements.define('malzauber-motif-gallery', MalzauberMotifGallery);
}
